import React, { useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FIREBASE_CONFIG, LAW_FIRM_INFO } from '../../config/Config';
import { findPortalClientByEmail, fetchDocumentById } from './CanonicalHtmlViewerRepository';
import { getConsentActionDescriptor, normalizeDocumentConsentState, resolveDocumentConsentFromClient } from '../../utils/documentConsent';
import { logClientPortalEventOnce } from '../../utils/portalEventLog';
import { resolveDocumentUrl } from '../../utils/documentResolver';

const appId = FIREBASE_CONFIG.projectId;

const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const resolveDocTitleFromText = (rawText, fallbackTitle) => {
  const text = String(rawText || '').trim();
  const prefix = 'Procuração publicada:';
  if (text.toLowerCase().startsWith(prefix.toLowerCase())) {
    const sliced = text.slice(prefix.length).trim();
    if (sliced) return sliced;
  }
  return String(fallbackTitle || '').trim() || 'Documento Publicado';
};

const formatWhenValue = (value) => {
  try {
    const date = value?.toDate ? value.toDate() : (value ? new Date(value) : null);
    if (!date || Number.isNaN(date.getTime())) return '';
    return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return '';
  }
};

const humanizeWhoValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return String(LAW_FIRM_INFO?.name || '').trim() || 'Sistema';

  const officeEmail = String(LAW_FIRM_INFO?.contact?.email || '').trim().toLowerCase();
  const officeName = String(LAW_FIRM_INFO?.name || '').trim();
  const officeFirstName = String(officeName.split(/\s+/)[0] || '').trim().toLowerCase();
  const rawLower = raw.toLowerCase();

  if (rawLower === 'operador' || rawLower === 'advogado') return officeName || raw;
  if (rawLower === 'sistema') return 'Sistema';
  if (officeEmail && rawLower === officeEmail) return officeName || raw;
  if (officeFirstName && rawLower === officeFirstName) return officeName || raw;

  if (raw.includes('@')) {
    const emailUser = String(raw.split('@')[0] || '').trim();
    if (emailUser && officeEmail && rawLower === officeEmail) return officeName || raw;
    if (emailUser) return emailUser.replace(/[._-]+/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()).trim();
  }

  return raw;
};

const buildCompactLine = (when, who) => {
  const safeWhen = String(when || '').trim();
  const safeWho = String(who || '').trim();
  if (safeWhen && safeWho) return `Documento publicado no sistema interno do Portal do Cliente em ${safeWhen} por ${safeWho}.`;
  if (safeWhen) return `Documento publicado no sistema interno do Portal do Cliente em ${safeWhen}.`;
  if (safeWho) return `Documento publicado no sistema interno do Portal do Cliente por ${safeWho}.`;
  return 'Documento publicado no sistema interno do Portal do Cliente.';
};

const resolvePortalClientName = (client) => {
  return (
    String(client?.fullName || '').trim() ||
    String(client?.qualification?.physical?.socialName || '').trim() ||
    String(client?.qualification?.physical?.name || '').trim() ||
    String(client?.contact?.name || '').trim() ||
    'Cliente'
  );
};

const isGoogleDriveUrl = (value) => {
  const raw = String(value || '').trim();
  return /(^https?:\/\/)?(drive|docs)\.google\.com\//i.test(raw);
};

const toGoogleDrivePreviewUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const fileMatch = raw.match(/\/file\/d\/([^/]+)/i);
  if (fileMatch?.[1]) return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;

  const openIdMatch = raw.match(/[?&]id=([^&#]+)/i);
  if (openIdMatch?.[1]) return `https://drive.google.com/file/d/${openIdMatch[1]}/preview`;

  return raw
    .replace(/\/view(?:\?[^#]*)?$/i, '/preview')
    .replace(/\/edit(?:\?[^#]*)?$/i, '/preview');
};

const isHtmlPublication = (documentData, resolvedUrl) => {
  const mimeType = String(documentData?.mimeType || '').trim().toLowerCase();
  const extension = String(documentData?.extension || '').trim().toLowerCase();
  const fileName = String(documentData?.fileName || '').trim().toLowerCase();
  const storagePath = String(documentData?.storagePath || documentData?.storage_path || '').trim().toLowerCase();
  const type = String(documentData?.type || '').trim().toUpperCase();
  const status = String(documentData?.status || '').trim().toUpperCase();
  const urlHint = String(resolvedUrl || '').trim().toLowerCase();

  if (mimeType === 'text/html') return true;
  if (extension === 'html' || extension === '.html') return true;
  if (fileName.endsWith('.html')) return true;
  if (storagePath.endsWith('.html')) return true;
  if (urlHint.endsWith('.html')) return true;
  if (type === 'PROCURATION' && status === 'PUBLISHED' && mimeType !== 'application/pdf') return true;
  return false;
};

const isPdfPublication = (documentData, resolvedUrl) => {
  const mimeType = String(documentData?.mimeType || '').trim().toLowerCase();
  const extension = String(documentData?.extension || '').trim().toLowerCase();
  const fileName = String(documentData?.fileName || '').trim().toLowerCase();
  const storagePath = String(documentData?.storagePath || documentData?.storage_path || '').trim().toLowerCase();
  const urlHint = String(resolvedUrl || '').trim().toLowerCase();

  if (mimeType === 'application/pdf') return true;
  if (extension === 'pdf' || extension === '.pdf') return true;
  if (fileName.endsWith('.pdf')) return true;
  if (storagePath.endsWith('.pdf')) return true;
  if (urlHint.includes('.pdf')) return true;
  if (urlHint.includes('firebasestorage.googleapis.com') && urlHint.includes('application%2Fpdf')) return true;
  return false;
};

const documentBelongsToPortalClient = (documentData, portalClient, portalEmail) => {
  if (!documentData || !portalClient) return false;

  const portalClientId = String(portalClient?.id || '').trim();
  const documentClientId = String(documentData?.clientId || documentData?.context?.primaryEntityId || '').trim();
  if (portalClientId && documentClientId && portalClientId === documentClientId) return true;

  const related = Array.isArray(documentData?.context?.relatedEntities) ? documentData.context.relatedEntities : [];
  if (portalClientId && related.some((value) => String(value || '').trim() === portalClientId)) return true;

  const emailCandidates = [
    documentData?.clientEmail,
    documentData?.context?.primaryEntityEmail,
    documentData?.contact?.email,
    portalClient?.contact?.email,
    portalClient?.email,
    portalEmail,
  ]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);

  const portalCandidates = new Set(
    [portalClient?.contact?.email, portalClient?.email, portalEmail]
      .map((value) => String(value || '').trim().toLowerCase())
      .filter(Boolean)
  );

  return emailCandidates.some((value) => portalCandidates.has(value));
};

export default function CanonicalHtmlViewer() {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const [srcDoc, setSrcDoc] = useState('');
  const [iframeUrl, setIframeUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [documentMeta, setDocumentMeta] = useState(null);
  const [portalClient, setPortalClient] = useState(null);
  const [portalEmail, setPortalEmail] = useState('');
  const [portalLoading, setPortalLoading] = useState(true);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentFeedback, setConsentFeedback] = useState('');

  const consentState = useMemo(() => normalizeDocumentConsentState(documentMeta || {}), [documentMeta]);
  const consentDescriptor = useMemo(() => getConsentActionDescriptor(documentMeta || {}), [documentMeta]);
  const isPortalClientViewer = useMemo(() => {
    return documentBelongsToPortalClient(documentMeta, portalClient, portalEmail);
  }, [documentMeta, portalClient, portalEmail]);

  useEffect(() => {
    let alive = true;
    const auth = getAuth();

    const loadPortalClient = async (email) => {
      const safeEmail = String(email || '').trim();
      if (!safeEmail) {
        if (!alive) return;
        setPortalClient(null);
        setPortalLoading(false);
        return;
      }

      setPortalLoading(true);
      try {
        const client = await findPortalClientByEmail(appId, safeEmail);
        if (!alive) return;
        setPortalClient(client);
      } finally {
        if (!alive) return;
        setPortalLoading(false);
      }
    };

    const initialEmail = String(auth?.currentUser?.email || '').trim();
    if (initialEmail) {
      setPortalEmail(initialEmail);
      void loadPortalClient(initialEmail);
    }

    const unsub = onAuthStateChanged(auth, (user) => {
      const nextEmail = String(user?.email || '').trim();
      setPortalEmail(nextEmail);
      void loadPortalClient(nextEmail);
    });

    return () => {
      alive = false;
      try {
        unsub && unsub();
      } catch (e) {
        void e;
      }
    };
  }, []);

  useEffect(() => {
    let alive = true;

    const boot = async () => {
      setLoading(true);
      setError('');
      setSrcDoc('');
      setIframeUrl('');

      try {
        let resolvedUrl = String(params.get('url') || '').trim();
        const documentId = String(params.get('documentId') || '').trim();
        const titleHint = String(params.get('title') || '').trim();
        let resolvedWho = String(params.get('who') || '').trim();
        let resolvedWhen = String(params.get('when') || '').trim();
        let documentData = null;

        if (documentId) {
          const data = await fetchDocumentById(appId, documentId);
          if (data) {
            documentData = data;
            if (!resolvedUrl) {
              resolvedUrl = String(await resolveDocumentUrl(data)).trim();
            }
          }
        }

        if (documentData) {
          if (!resolvedWhen) {
            resolvedWhen = formatWhenValue(
              documentData?.publishedAt ||
              documentData?.publication?.publishedAt ||
              documentData?.publication?.publishedAtIso ||
              documentData?.createdAt ||
              documentData?.updatedAt ||
              documentData?.signedAt
            );
          }

          if (!resolvedWho) {
            resolvedWho = humanizeWhoValue(
              documentData?.publishedByName ||
              documentData?.publication?.publishedBy ||
              documentData?.createdByName ||
              documentData?.createdBy ||
              documentData?.authorName ||
              documentData?.author ||
              documentData?.actor?.name ||
              documentData?.uploadedByName ||
              ''
            );
          }
        }

        const safeTitle = String(
          titleHint ||
          documentData?.title ||
          documentData?.name ||
          documentData?.fileName ||
          'Documento Publicado'
        ).trim() || 'Documento Publicado';
        const htmlFromDocument = String(documentData?.htmlContent || documentData?.html || '').trim();
        const shouldRenderSavedHtml = Boolean(htmlFromDocument) && isHtmlPublication(documentData, resolvedUrl);
        const shouldRenderPdf = isPdfPublication(documentData, resolvedUrl);

        if (shouldRenderSavedHtml) {
          const parser = new DOMParser();
          const parsed = parser.parseFromString(htmlFromDocument, 'text/html');
          const legacyBanner = parsed.querySelector('.publication-banner');
          const titleFromLegacyBanner = String(legacyBanner?.querySelector('h1')?.textContent || '').trim();
          const textFromLegacyBanner = String(legacyBanner?.textContent || '').trim();
          const finalTitle = resolveDocTitleFromText(textFromLegacyBanner, safeTitle || titleFromLegacyBanner);
          const compactLine = buildCompactLine(resolvedWhen, resolvedWho);

          if (legacyBanner) {
            legacyBanner.innerHTML = `<h1>${escapeHtml(finalTitle)}</h1><p>${escapeHtml(compactLine)}</p>`;
          } else {
            const shell = parsed.querySelector('.published-shell') || parsed.body;
            const banner = parsed.createElement('div');
            banner.className = 'publication-banner';
            banner.innerHTML = `<h1>${escapeHtml(finalTitle)}</h1><p>${escapeHtml(compactLine)}</p>`;
            shell?.insertBefore(banner, shell.firstChild || null);
          }

          parsed.querySelectorAll('.publication-banner ul').forEach((node) => node.remove());
          parsed.querySelectorAll('.publication-banner li').forEach((node) => node.remove());

          const finalHtml = '<!doctype html>\n' + (parsed.documentElement?.outerHTML || htmlFromDocument);
          if (alive) {
            setSrcDoc(finalHtml);
            setDocumentMeta({
              id: documentId,
              documentId,
              ...(documentData || {}),
              title: finalTitle,
              fileUrl: resolvedUrl,
              publicationWhen: resolvedWhen,
              publicationWho: resolvedWho,
            });
          }
          return;
        }

        if (shouldRenderPdf) {
          if (!resolvedUrl) throw new Error('URL do PDF não encontrada.');
          if (alive) {
            setIframeUrl(resolvedUrl);
            setDocumentMeta({
              id: documentId,
              documentId,
              ...(documentData || {}),
              title: safeTitle,
              fileUrl: resolvedUrl,
              publicationWhen: resolvedWhen,
              publicationWho: resolvedWho,
            });
          }
          return;
        }

        if (!resolvedUrl) throw new Error('Conteúdo HTML do documento não encontrado.');

        const response = await fetch(resolvedUrl, { method: 'GET' });
        if (!response.ok) throw new Error(`Falha ao carregar documento: HTTP ${response.status}`);

        const htmlText = String(await response.text() || '').trim();
        if (!htmlText) throw new Error('Documento HTML vazio.');

        const parser = new DOMParser();
        const parsed = parser.parseFromString(htmlText, 'text/html');
        const legacyBanner = parsed.querySelector('.publication-banner');
        const titleFromLegacyBanner = String(legacyBanner?.querySelector('h1')?.textContent || '').trim();
        const textFromLegacyBanner = String(legacyBanner?.textContent || '').trim();
        const finalTitle = resolveDocTitleFromText(textFromLegacyBanner, safeTitle || titleFromLegacyBanner);
        const compactLine = buildCompactLine(resolvedWhen, resolvedWho);

        if (legacyBanner) {
          legacyBanner.innerHTML = `<h1>${escapeHtml(finalTitle)}</h1><p>${escapeHtml(compactLine)}</p>`;
        } else {
          const shell = parsed.querySelector('.published-shell') || parsed.body;
          const banner = parsed.createElement('div');
          banner.className = 'publication-banner';
          banner.innerHTML = `<h1>${escapeHtml(finalTitle)}</h1><p>${escapeHtml(compactLine)}</p>`;
          shell?.insertBefore(banner, shell.firstChild || null);
        }

        parsed.querySelectorAll('.publication-banner ul').forEach((node) => node.remove());
        parsed.querySelectorAll('.publication-banner li').forEach((node) => node.remove());

        const finalHtml = '<!doctype html>\n' + (parsed.documentElement?.outerHTML || htmlText);
        if (alive) {
          setSrcDoc(finalHtml);
          setDocumentMeta({
            id: documentId,
            documentId,
            ...(documentData || {}),
            title: finalTitle,
            fileUrl: resolvedUrl,
            publicationWhen: resolvedWhen,
            publicationWho: resolvedWho,
          });
        }
      } catch (e) {
        if (alive) setError(String(e?.message || e || 'Falha ao carregar documento'));
      } finally {
        if (alive) setLoading(false);
      }
    };

    boot();
    return () => {
      alive = false;
    };
  }, [params]);

  const resolveConsentActorName = () => {
    const parties = Array.isArray(documentMeta?.parties) ? documentMeta.parties : [];
    const docClientId = String(documentMeta?.clientId || documentMeta?.context?.primaryEntityId || '').trim();
    if (docClientId && parties.length > 0) {
      const match = parties.find((p) => String(p?.clientId || '').trim() === docClientId);
      const name = String(match?.principalName || match?.signerName || match?.name || '').trim();
      if (name) return name;
    }
    const directName = String(documentMeta?.clientName || '').trim();
    if (directName) return directName;
    return resolvePortalClientName(portalClient);
  };

  const handleClientConsentDecision = async (decision) => {
    const documentId = String(documentMeta?.documentId || documentMeta?.id || '').trim();
    if (!documentId || consentSaving) return;

    setConsentSaving(true);
    setConsentFeedback('');
    try {
      const result = await resolveDocumentConsentFromClient({
        documentId,
        decision,
        actorName: resolveConsentActorName(),
      });

      if (!result?.ok) {
        setConsentFeedback(String(result?.reason || 'Não foi possível registrar a sua resposta.'));
        return;
      }

      setDocumentMeta((prev) => ({ ...(prev || {}), ...(result?.data || {}) }));
      setConsentFeedback(decision === 'declined' ? 'Consentimento recusado com sucesso.' : 'Consentimento confirmado com sucesso.');
    } catch (e) {
      setConsentFeedback(String(e?.message || e || 'Falha ao registrar consentimento.'));
    } finally {
      setConsentSaving(false);
    }
  };

  useEffect(() => {
    const documentId = String(documentMeta?.documentId || documentMeta?.id || '').trim();
    const clientId = String(portalClient?.id || '').trim();
    if (!documentId || !clientId || !isPortalClientViewer) return;

    (async () => {
      try {
        const title = String(documentMeta?.title || '').trim() || 'Documento Publicado';
        await logClientPortalEventOnce({
          clientId,
          type: 'CLIENT_DOCUMENT_VIEWED',
          actorName: resolvePortalClientName(portalClient),
          payload: {
            documentId,
            title,
            fileUrl: String(documentMeta?.fileUrl || '').trim(),
            text: `Documento visualizado pelo cliente: ${title}`,
          },
          storage: 'session',
          dedupeKey: `doc_view:${documentId}`,
        });
      } catch (e) {
        void e;
      }
    })();
  }, [documentMeta?.documentId, documentMeta?.fileUrl, documentMeta?.id, documentMeta?.title, isPortalClientViewer, portalClient]);

  useEffect(() => {
    if (params.get('print') !== '1' || loading || error) return;
    const timer = setTimeout(() => {
      try { window.print(); } catch (e) { void e; }
    }, 1200);
    return () => clearTimeout(timer);
  }, [loading, error, params]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center text-sm text-slate-600">Carregando documento...</div>;
  }

  if (error) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6"><div className="max-w-lg w-full rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div></div>;
  }

  return (
    <div className="w-full h-screen bg-slate-100 flex flex-col">
      {!portalLoading && isPortalClientViewer && consentState.requiresConsent ? (() => {
        const docCategory = String(documentMeta?.category || documentMeta?.classification?.category || documentMeta?.docType || documentMeta?.title || '').toLowerCase();
        const isSignatureDoc = docCategory.includes('procura') || docCategory.includes('contrat');
        return (
          <div className="border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
            <div className="mx-auto w-full max-w-6xl flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-widest text-slate-500">
                  {isSignatureDoc ? 'Documento para Assinatura' : 'Consentimento Informado'}
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-700">
                  {isSignatureDoc ? (consentState.isReviewPending ? 'Em Análise pelo Escritório' : consentState.isPending ? 'Leitura Prévia — Assine via Acervo' : consentDescriptor.label) : consentDescriptor.label}
                </div>
                {consentFeedback ? <div className="mt-1 text-xs font-semibold text-slate-500">{consentFeedback}</div> : null}
              </div>

              {isSignatureDoc && consentState.isPending ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      try { window.close(); } catch (e) { void e; }
                      setTimeout(() => {
                        try { window.location.href = '/?page=portal-documentos'; } catch (e2) { void e2; }
                      }, 300);
                    }}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    Fechar e Voltar ao Acervo
                  </button>
                </div>
              ) : null}

              {!isSignatureDoc && consentState.isPending ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => handleClientConsentDecision('accepted')}
                    disabled={consentSaving}
                    className={
                      'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ' +
                      (consentSaving
                        ? 'border-emerald-200 bg-emerald-100 text-emerald-700 cursor-wait'
                        : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100')
                    }
                  >
                    {consentSaving ? 'Salvando...' : 'Aceitar Consentimento'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClientConsentDecision('declined')}
                    disabled={consentSaving}
                    className={
                      'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ' +
                      (consentSaving
                        ? 'border-red-200 bg-red-100 text-red-700 cursor-wait'
                        : 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100')
                    }
                  >
                    {consentSaving ? 'Salvando...' : 'Recusar Consentimento'}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        );
      })() : null}

      <div className="flex-1">
        <iframe title="Visualizador Canônico" src={iframeUrl || undefined} srcDoc={iframeUrl ? undefined : srcDoc} className="w-full h-full border-0" />
      </div>
    </div>
  );
}
